import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../ai-omnibar/ai-capability-registry";

/**
 * What the newer Clock in & out list can do for the AI omnibar.
 *
 * The older Angular page at /app/evv/clockedVisits already declares `clockedVisits.filter`; this is
 * the GraphQL rewrite, with its own filters, so it gets its own id rather than sharing one - the two
 * pages are never on screen together, and a shared id would have meant two runners for one capability.
 */
declareAiCapability({
  id: "clockedVisitsNew.filter",
  title: "Filter the clock in & out list (new)",
  effect: "prefill",
  description:
    "Sets the filters on EVV > Clock in & out (New): the visit date range, contract, office, coordinator, team, certification, and a free-text search over patient and caregiver names. The page opens on today. Only works while that page is open.",
  location: "the filter bar of the EVV > Clock in & out (New) page",
  scope: "page",
  permissionKey: "view_evv_clock_in_out",
  params: [
    {
      name: "from",
      type: "date",
      required: false,
      description: "First visit day to include, as yyyy-MM-dd.",
    },
    {
      name: "to",
      type: "date",
      required: false,
      description: "Last visit day to include, as yyyy-MM-dd.",
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these contracts.",
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these offices.",
    },
    {
      name: "coordinator",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to visits whose patient has one of these coordinators.",
    },
    {
      name: "team",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these teams.",
    },
    {
      name: "certification",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to caregivers holding these certifications.",
    },
    {
      name: "search",
      type: "text",
      required: false,
      searchesFreeText: true,
      description: "Free text matched against patient and caregiver names.",
    },
  ],
  examples: [
    "clock in and out yesterday",
    "clock ins for the europe office last week",
    "clocked visits on the france contract",
  ],
});

function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("clockedVisitsNew.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

export function useClockedVisitsNewAiCapabilities(params: {
  /** The page's own setFilter, typed loosely: this hook only sets keys it declares. */
  setFilter: (key: string, value: unknown) => void;
}): void {
  const { setFilter } = params;
  const { queries } = useApi();

  const setFilterRef = React.useRef(setFilter);
  setFilterRef.current = setFilter;

  // The same queries the filter bar's own selects use, so this reads their cache rather than
  // fetching anything of its own.
  const offices = useQuery(queries.office.list());
  const contracts = useQuery(queries.contract.list());
  const teams = useQuery(queries.agencyTeam.list());
  const agencyMembers = useQuery(queries.agencyMember.list());
  const certifications = useQuery(queries.caregiver.certifications());

  useContributedOptions("office", offices.data?.offices);
  useContributedOptions("contract", contracts.data?.contractTypes);
  useContributedOptions("team", teams.data?.agencyTeams);
  useContributedOptions(
    "coordinator",
    React.useMemo(
      () =>
        (agencyMembers.data?.agencyMembers ?? []).map((member) => ({
          id: member.id,
          name: [member.firstName, member.lastName].filter(Boolean).join(" "),
        })),
      [agencyMembers.data?.agencyMembers]
    )
  );
  useContributedOptions(
    "certification",
    React.useMemo(
      () =>
        (certifications.data ?? []).map((row) => ({
          id: row.certification,
          name: row.certification,
        })),
      [certifications.data]
    )
  );

  // Not available until the lists have arrived: an empty option list turns a real filter into a
  // free-text search, so "europe" would land in the search box instead of the office filter.
  const listsLoaded =
    offices.isSuccess && contracts.isSuccess && teams.isSuccess && agencyMembers.isSuccess;

  useProvideAiCapability(
    "clockedVisitsNew.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const label = (name: string) => context.labels[name] ?? String(args[name]);

      if (args.from !== undefined) {
        setFilterRef.current("from", String(args.from));
      }

      if (args.to !== undefined) {
        setFilterRef.current("to", String(args.to));
      }

      if (args.from !== undefined || args.to !== undefined) {
        applied.push([args.from, args.to].filter(Boolean).join(" to "));
      }

      for (const [arg, key, numeric] of [
        ["contract", "contracts", true],
        ["office", "offices", true],
        ["coordinator", "coordinators", true],
        ["team", "teams", true],
        ["certification", "certifications", false],
      ] as const) {
        if (args[arg] === undefined) {
          continue;
        }

        setFilterRef.current(
          key,
          asList(args[arg]).map((one) => (numeric ? Number(one) : String(one)))
        );
        applied.push(label(arg));
      }

      if (args.search !== undefined) {
        setFilterRef.current("search", String(args.search));
        applied.push(`"${String(args.search)}"`);
      }

      return applied.length === 0
        ? "Re-applied the clock in & out filters"
        : `Showing clock in & out: ${applied.join(", ")}`;
    },
    { enabled: listsLoaded }
  );
}
